import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Award, Hammer } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1567981231971-228a0e25ba82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYXJjaGl0ZWN0dXJlJTIwcm9vZiUyMHRpbGVzJTIwcm9vZmVycyUyMGNyYWZ0c21hbnNoaXAlMjBjb3BwZXIlMjByb29mfGVufDB8fHxibGFja3wxNzkyNzkwMDYyfDA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 20 });
  const sy = useSpring(my, { stiffness: 55, damping: 20 });
  const bgX = useTransform(sx, [-0.5, 0.5], [-26, 26]);
  const bgY = useTransform(sy, [-0.5, 0.5], [-18, 18]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scrollDrift = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      className="relative min-h-screen overflow-hidden flex items-center pt-32 pb-24 bg-[#0B0C0E]"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-[-4%]" style={{ y: scrollDrift, opacity: fade }} aria-hidden="true">
        <motion.div className="absolute inset-0" style={{ x: bgX, y: bgY }}>
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover scale-[1.08]"
            loading="eager"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E]/80 via-[#0B0C0E]/60 to-[#0B0C0E]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(11,12,14,0.55)_100%)]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-[#D97736] mb-6"
        >
          Eccellenza nelle coperture dal 1988
        </motion.p>

        <h1
          data-testid="hero-title-masked"
          className="font-syne font-extrabold tracking-tight leading-[1.08] text-[1.9rem] sm:text-6xl lg:text-7xl max-w-5xl"
        >
          <Line delay={0.25}>L'arte della copertura:</Line>
          <Line delay={0.38}>
            protezione, durata ed
          </Line>
          <Line delay={0.51}>
            <span className="text-[#D97736]">eleganza architettonica.</span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-7 max-w-2xl text-base sm:text-lg text-[#9CA3AF] font-light leading-relaxed"
        >
          Specialisti in impermeabilizzazione in guaina, coperture in tegole tradizionali e pannelli
          finto coppo coibentati ad alta efficienza energetica.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-preventivo"
            onClick={() => scrollToId("#preventivo")}
            className="group flex items-center gap-3 bg-[#D97736] hover:bg-[#E88D52] text-[#0B0C0E] font-outfit font-semibold rounded-full px-7 py-4 text-base transition-colors duration-300"
          >
            Richiedi Preventivo Gratuito
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            href="tel:+393517479343"
            data-testid="hero-cta-phone"
            className="flex items-center gap-3 border border-white/15 hover:border-[#D97736]/60 text-[#F3F4F6] font-mono text-sm rounded-full px-7 py-4 backdrop-blur-sm transition-colors duration-300"
          >
            <Phone size={16} className="text-[#D97736]" />
            Chiama: +39 351 747 9343
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
        >
          {[
            { icon: ShieldCheck, label: "Garanzia scritta 20 anni" },
            { icon: Award, label: "Posa certificata a regola d'arte" },
            { icon: Hammer, label: "Sopralluogo gratuito in 48h" },
          ].map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-[#6B7280]">
              <Icon size={14} className="text-[#D97736]" />
              {label}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
